// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

export interface SideTab { value: string; label: string; icon?: ReactNode; meta?: ReactNode }

/**
 * D-UI-3 — section navigation for a page with several views: a sticky left rail on wide screens, a
 * scrollable tab strip on narrow ones. One section is shown at a time, so the reader never scrolls
 * past four tables to find the fifth.
 */
export default function SideTabs({ value, onChange, tabs, ariaLabel, children }: {
  value: string; onChange: (value: string) => void; tabs: SideTab[]; ariaLabel: string; children: ReactNode
}) {
  const wide = useMediaQuery(useTheme().breakpoints.up('md'))
  if (!wide) return (
    <Box>
      <Tabs value={value} onChange={(_, v: string) => onChange(v)} variant="scrollable" aria-label={ariaLabel}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', minHeight: 40 } }}>
        {tabs.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>
      {children}
    </Box>
  )
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', gap: 2.5, alignItems: 'start' }}>
      <Box component="nav" aria-label={ariaLabel} sx={{ position: 'sticky', top: 12 }}>
        <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
          {tabs.map(t => {
            const on = t.value === value
            return (
              <ListItemButton key={t.value} selected={on} aria-current={on ? 'page' : undefined} onClick={() => onChange(t.value)}
                sx={{ py: 1.1, gap: 1.25, borderLeft: 3, borderColor: on ? 'primary.main' : 'transparent', alignItems: 'flex-start',
                      '&.Mui-selected': { bgcolor: 'action.selected' } }}>
                {t.icon && <Box sx={{ mt: '2px', color: on ? 'primary.main' : 'text.secondary', display: 'flex' }}>{t.icon}</Box>}
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 13, fontWeight: on ? 700 : 500 }}>{t.label}</Box>
                  {t.meta && <Box sx={{ fontSize: 11.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{t.meta}</Box>}
                </Box>
              </ListItemButton>
            )
          })}
        </List>
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  )
}
